import React, { lazy, useEffect, useMemo, useRef, useState } from "react";
import { createDraft, updateQuote } from "../../../backend-services";
import { Button, SidebarDrawer } from "../../common";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ICreateSalesData,
  ICustomer,
  IInventory,
  ItemInput,
} from "../../../@types";
import {
  createDraftData,
  createInvoiceData,
  createQuoteData,
  InvoiceValidationErrors,
} from "../../../modules/invoice/invoice";
import {
  InvoiceErrors,
  validateInvoiceBasicInfo,
  validateInvoiceItemDetails,
  validateInvoiceSummary,
} from "../../../modules/validateInvoiceForm";
import { clearFieldError, handleErrors } from "../../../utils/handleErrors";
import useLineItems from "../../../modules/lineItem";
import { useUserStore } from "../../../state";
import LogoComponent from "../create/SalesCreate/LogoComponent";
import LineTable from "../create/SalesCreate/LineTable";
import InvoiceCalculation from "../create/SalesCreate/InvoiceCalculation";
import BankDetails from "../create/SalesCreate/BankDetails";
import Notes from "../create/SalesCreate/Notes";
import CustomerCreateInfo from "../create/SalesCreate/CustomerInfo";
import { updateProfile } from "../../../backend-services";
import CreateInvoice from "../create/SalesCreate/CreateInvoice";
import QuoteDate from "../create/SalesCreate/QuoteDate";
import {
  validateQuoteBasicInfo,
  validateQuoteItemDetails,
  validateQuoteSummary,
} from "../../../modules/validateQuoteForm";
import InvoiceCurrency from "../create/SalesCreate/InvoiceCurrency";
import { CreateModal } from "../../common/CreateModal";
import SuccessBanner from "../../common/Banner/SuccessBanner";
import { useTranslation } from "react-i18next";
import CompanyUpdate from "../create/SalesCreate/companyProfile";
import { checkSubscription } from "../../../utils/subscription";
import { servicesType } from "../../../constants/Subscription";
import CreateQuote from "../create/SalesCreate/CreateQuote";
import { useCompanyStore } from "../../../state/useCompanyStore";
import { useGetPaymentCurrencyList } from "../../../utils";
import LineItemsSection from "../../Invoice/sections/LineItemsSection";
import ServiceLineItem from "../../common/LineItemTable/ServiceLineItemTable/ServiceLineItem";
import { ServiceLineItemMobile } from "../../common/LineItemTable/ServiceLineItemTable/ServiceLineItemMobile";
import QuoteServiceLine from "./QuoteServiceLine";
import { showToast } from "../../../utils/toastUtils";

interface QuoteFormProps {
  customerId?: string;
  quoteData: ICreateSalesData;
  setQuoteData: React.Dispatch<React.SetStateAction<ICreateSalesData>>;
  headingText: string;
  updatedNewItem?: ItemInput;
}
const QuoteParent: React.FC<QuoteFormProps> = (props) => {
  const { t } = useTranslation("quotes");
  const userStore = useCompanyStore();
  const { userCurrencyCode, currencyList } = useGetPaymentCurrencyList(
    userStore.company,
  );
  const isRetail = userStore.isRetailBusiness();
  const initialFormData: ICreateSalesData = {
    additionalInformation: "",
    customerId: "",
    description: "",
    currency: userCurrencyCode.currency,
    discount: 0,
    inventories: [
      {
        id: "",
        name: "",
        amount: 0,
        quantity: 0,
        rate: 0,
        variations: [
          {
            type: "",
            name: "",
            id: "",
            price: 0,
            quantity: 0,
          },
        ],
      },
    ],
    services: [{ id: "", name: "", duration: "", price: 0 }],
    expiryDate: "",
    invoiceNumber: "",
    issueDate: new Date().toISOString().substring(0, 10),
    isReoccurring: false,
    isReoccurringEndDate: "",
    isReoccurringStartDate: "",
    reoccurringGroupName: "",
    status: "pending",
    tax: 0,
    shippingCharge: 0,
    shippingDesc: "",
    hasShipping: true,
    isPaymentCompleted: false,
    shouldSendEmail: true,
    exchangeRate: 1,
    paymentMethod: "",
    invoiceType: "inventory",
    logoBase64: "",
  };

  const [validationResults, setValidationResults] =
    React.useState<InvoiceErrors>({});
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = React.useState<ICustomer | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDraftLoading, setIsisDraftLoading] = React.useState<boolean>(false);
  const [subTotal, setSubtotal] = React.useState<number>(0);
  const [total, setTotal] = React.useState<number>(0);
  const [calcTax, setCalcTax] = React.useState(0);
  const [calcShipping, setCalcShipping] = React.useState(0);
  const [calcDiscount, setCalcDiscount] = React.useState(0);
  const [showSidebar, setShowSidebar] = React.useState(false);
  const [showCreateSidebar, setShowCreateSidebar] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [createdQuoteId, setCreatedQuoteId] = React.useState("");
  const [createdQuote, setCreatedQuote] = useState(null);
  const [createdEmail, setCreatedEmail] = React.useState("");
  const [showRateInput, setShowRateInput] = useState(false);
  const [markAsPaid, setMarkAsPaid] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(false);
  const [shouldSendWhatsApp, setShouldSendWhatsApp] = React.useState(false);
  const [shouldSendSMS, setShouldSendSMS] = React.useState(false);
  const [selectedCurrencyLabel, setSelectedCurrencyLabel] = React.useState(
    userCurrencyCode.currency,
  );
  const [selectedVariation, setSelectedVariation] = useState<
    string | undefined
  >(undefined);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const [error, setError] = React.useState<string>("");
  const [isReoccurringChecked, setIsReoccurringChecked] = React.useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerLinkText, setBannerLinkText] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [serviceType, setServiceType] = React.useState<string>("time-based");

  const [draftValidationResults, setDraftValidationResults] =
    React.useState<InvoiceValidationErrors>({
      customerId: undefined,
      invoiceNumber: undefined,
      invoiceDueAt: "",
      description: undefined,
      status: undefined,
      issueDate: undefined,
      additionalInformation: undefined,
      shippingCharge: undefined,
      shippingDesc: undefined,
      discount: undefined,
      tax: undefined,
      isReoccurring: undefined,
      isReoccurringStartDate: undefined,
      isReoccurringEndDate: undefined,
      reoccurringGroupName: undefined,
      task: undefined,
      inventories: undefined,
      services: undefined,
      currency: undefined,
      exchangeRate: undefined,
      paymentMethod: undefined,
      invoiceType: undefined,
    });
  const { items, addItem, setItems, removeItem, updateCell, updateRow } =
    useLineItems([{ id: "", name: "", rate: 0, quantity: 0 }]);
  const { quoteNo } = useParams<{ quoteNo: string }>();
  const [isEditing, setIsEditing] = useState<boolean>(!!quoteNo);

  const [formData, setFormData] =
    React.useState<ICreateSalesData>(initialFormData);

  React.useEffect(() => {
    if (props.quoteData) {
      setFormData((prevState: any) => ({
        ...prevState,
        customerId: props.quoteData.customerId,
        customer: props.quoteData.customerId ? customer : null,
        description: props.quoteData.description,
        discount: props.quoteData.discount,
        inventories: props.quoteData.inventories,
        services: props.quoteData.services,
        expiryDate: props.quoteData.expiryDate,
        issueDate: props.quoteData.issueDate,
        shippingCharge: props.quoteData.shippingCharge,
        status: props.quoteData.status,
        tax: props.quoteData.tax,
        shouldSendEmail: props.quoteData.shouldSendEmail,
        currency: props.quoteData.currency,
        exchangeRate: props.quoteData.exchangeRate,
        invoiceType: props.quoteData.invoiceType,
      }));
    }
  }, [props.quoteData]);

  const resetForm = () => {
    setFormData({
      ...(initialFormData as ICreateSalesData),
      invoiceDueAt: "",
      additionalInformation: "",
    });
    setValidationResults({});
    setCustomer(null);

    // Reset line items (inventories and services)
    setItems([{ id: "", name: "", rate: 0, quantity: 0 }]);
    setShowSidebar(false);
  };

  // Sync items to formData for desktop
  // Sync items to formData
  useEffect(() => {
    if (!isMobile && !isEditing) {
      setFormData((prevState) => {
        const newInventories = items.map((item) => ({
          id: item.id,
          name: item.name,
          amount: (item.quantity || 0) * (item.rate || 0),
          quantity: item.quantity || 0,
          rate: item.rate || 0,
          variations:
            item.variations?.map((variation) => ({
              ...variation,
              price: variation.price || 0,
              quantity: variation.quantity || 0,
            })) || [],
        }));

        // Only update if values have changed
        if (
          JSON.stringify(newInventories) !==
          JSON.stringify(prevState.inventories)
        ) {
          return {
            ...prevState,
            inventories: isRetail ? newInventories : prevState.inventories,
            services: isRetail ? prevState.services : items,
          };
        }

        return prevState;
      });
    }
  }, [items, isMobile, isRetail, isEditing]);

  React.useEffect(() => {
    clearFieldError("inventories", validationResults, setValidationResults);
    setFormData((prevState: ICreateSalesData) => {
      return {
        ...prevState,
        inventories: items.map((item: ItemInput) => ({
          id: item.id,
          name: item.name,
          amount: 0,
          quantity: item.quantity || 0,
          rate: item.rate || 0,
          variations:
            item.variations?.map((variation) => ({
              ...variation,
              price: variation.price || 0,
              quantity: variation.quantity || 0,
            })) || [],
        })),
      } as ICreateSalesData;
    });
  }, [items, isEditing]);

  React.useEffect(() => {
    if (!isEditing) {
      clearFieldError("services", validationResults, setValidationResults);
      setFormData((prevState: ICreateSalesData) => ({
        ...prevState,
        services: items,
      }));
    }
  }, [items, isEditing]);

  const handleInputChange = (event: { target: { name: any; value: any } }) => {
    const { name, value } = event.target;
    setFormData((prevData) => {
      return { ...prevData, [name]: value };
    });
    clearFieldError(name, validationResults, setValidationResults);
  };

  const calculatedValues = useMemo(() => {
    let subTotal = 0;

    if (isRetail) {
      const inventoriesToUse = isMobile ? formData?.inventories || [] : items;

      for (const inventory of inventoriesToUse) {
        if (!inventory) continue;

        if (inventory.variations && inventory.variations.length > 0) {
          if (isMobile) {
            for (const variation of inventory.variations) {
              if (!variation) continue;
              subTotal +=
                (Number(variation.quantity) || 0) *
                (Number(variation.price) || 0);
            }
          } else {
            subTotal +=
              (Number(inventory.quantity) || 0) * (Number(inventory.rate) || 0);
          }
        } else {
          subTotal +=
            (Number(inventory.quantity) || 0) * (Number(inventory.rate) || 0);
        }
      }
    } else {
      const servicesToUse = formData?.services || [];

      for (const service of servicesToUse) {
        if (!service) continue;

        let priceValue = 0;

        if (Array.isArray(service.price)) {
          const currencyPrice = service.price.find(
            (p: { currency: string | undefined }) =>
              p.currency === formData.currency,
          );
          priceValue = currencyPrice ? Number(currencyPrice.amount) || 0 : 0;
        } else {
          priceValue = Number(service.price) || 0;
        }

        subTotal += priceValue;
      }
    }

    const discount = Number(formData?.discount) || 0;
    const tax = Number(formData?.tax) || 0;

    const discountAmount = subTotal * (discount / 100);
    const taxAmount = subTotal * (tax / 100);
    const shippingAmount = Number(formData?.shippingCharge) || 0;
    const total = subTotal - discountAmount + taxAmount + shippingAmount;

    return {
      subTotal,
      total,
      calcTax: taxAmount,
      calcDiscount: discountAmount,
      calcShipping: shippingAmount,
    };
  }, [
    formData.discount,
    formData.tax,
    formData.shippingCharge,
    formData.currency,
    isRetail
      ? JSON.stringify(isMobile ? formData?.inventories : items)
      : JSON.stringify(formData?.services),
    isMobile,
    isRetail,
  ]);

  // 1. Update the component to use the values from useMemo
  useEffect(() => {
    const { subTotal, total, calcTax, calcDiscount } = calculatedValues;

    // Update all state variables with calculated values
    setSubtotal(subTotal);
    setTotal(total);
    setCalcTax(calcTax);
    setCalcDiscount(calcDiscount);
    setCalcShipping(calcShipping);
    // Update formData with total
    setFormData((prevData) => ({
      ...prevData,
      total: total.toFixed(2).toString(),
    }));
  }, [calculatedValues]);

  useEffect(() => {
    setFormData((prevData) => {
      return { ...prevData, total: total.toFixed(2).toString() };
    });
  }, [total]);

  useEffect(() => {
    if (isEditing && props.quoteData) {
      const { inventories, services } = props.quoteData;
      setItems(isRetail ? inventories : services);
    }
  }, [isEditing, props.quoteData, isRetail]);

  useEffect(() => {
    handleDropDownChange("currency", selectedCurrencyLabel);
  }, []);

  const handleDropDownChange = (name: string, value: string) => {
    clearFieldError(name, validationResults, setValidationResults);
    const currencyValue =
      value === userCurrencyCode.currency ? userCurrencyCode.currency : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: currencyValue || userCurrencyCode.currency,
      exchangeRate:
        currencyValue !== userCurrencyCode.currency ? prevData.exchangeRate : 1,
    }));
    setSelectedCurrencyLabel(value);
    setShowRateInput(currencyValue !== userCurrencyCode.currency);
  };

  const location = useLocation();
  const { customerId } = useParams();
  React.useEffect(() => {
    if (customerId) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        name: decodeURIComponent(customerId || ""),
      }));
    }
  }, [customerId]);

  const deepCompare = (arr1: any[], arr2: any[]) => {
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item, index) => {
      return JSON.stringify(item) === JSON.stringify(arr2[index]);
    });
  };

  const prevItemsRef = useRef<ItemInput[]>([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isEditing && props.quoteData) {
      const {
        customer,
        inventories = [],
        services = [],
        customerId,
        currency,
        status,
        exchangeRate,
      } = props.quoteData;

      setFormData((prev) => ({
        ...prev,
        customerId: customerId || "",
        currency: currency || userCurrencyCode.currency,
        status: status || "active",
        exchangeRate: exchangeRate || 1,
        inventories: inventories || [],
        services: services || [],
      }));

      if (customer) {
        setCustomer(customer);
      }
    }
  }, [isEditing, props.quoteData]);

  const handleSetCustomer = (selectedCustomer: ICustomer | null) => {
    if (selectedCustomer) {
      setCustomer(selectedCustomer);
      if (selectedCustomer.id) {
        setFormData((prevFormData: any) => ({
          ...prevFormData,
          customerId: selectedCustomer.id,
          name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        }));
      }
    } else {
      setCustomer(null);
      setFormData((prevFormData: any) => ({
        ...prevFormData,
        customerId: "",
        name: "",
      }));
    }
  };

  const setLogoUrl = (url: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      logoBase64: url,
    }));
  };

  const handleEditProfileLogo = async () => {
    setIsLoading(true);
    try {
      const updatedLogo = { brandLogo: formData.logoBase64 };
      await updateProfile(updatedLogo);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setErrorMessage("Unable to update profile logo.");
    }
  };

  useEffect(() => {
    if (
      typeof formData.exchangeRate !== "number" &&
      isNaN(Number(formData.exchangeRate))
    ) {
      // Set the exchange rate to 1
      setFormData((prevData) => ({
        ...prevData,
        exchangeRate: 1,
      }));
    }
  }, [formData.exchangeRate]);

  const handleViewQuote = () => {
    if (createdQuote) {
      const viewQuoteRoute = `/quotes/${createdQuote}/view`;
      navigate(viewQuoteRoute);
    }
  };

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
  };

  const normalizeServicePrice = (service: any, currency: string) => {
    if (!service) return service;

    let normalizedPrice = 0;

    if (Array.isArray(service.price)) {
      const currencyPrice = service.price.find(
        (p: { currency: string }) => p.currency === currency,
      );
      normalizedPrice = currencyPrice ? Number(currencyPrice.amount) || 0 : 0;
    } else {
      normalizedPrice = Number(service.price) || 0;
    }

    return {
      ...service,
      price: normalizedPrice,
    };
  };

  const processCreateQuote = async (
    sendEmail: boolean,
    markedAsPaid: boolean,
    shouldSendWhatsApp: boolean,
    shouldSendSMS: boolean,
    quoteNo?: string,
  ) => {
    try {
      setIsLoading(true);
      setShowCreateSidebar(false);

      const {
        expiryDate,
        issueDate,
        inventories,
        services,
        discount,
        tax,
        customerId,
      } = formData;
      const user = userStore.company;
      const isRetail = userStore.isRetailBusiness();
      const validExpiry = expiryDate ? new Date(expiryDate) : null;
      const validIssueDate = issueDate ? new Date(issueDate) : null;

      if (!validExpiry || isNaN(validExpiry.getTime())) {
        throw new Error("Invalid expiry date");
      }

      if (!validIssueDate || isNaN(validIssueDate.getTime())) {
        throw new Error("Invalid issue date");
      }

      const quoteData: ICreateSalesData = {
        customerId: customer?.id || customerId,
        shouldSendEmail: sendEmail,
        isPaymentCompleted: markedAsPaid,
        shouldSendWhatsApp: shouldSendWhatsApp,
        shouldSendSMS: shouldSendSMS,
        expiryDate: validExpiry.toISOString(),
        issueDate: validIssueDate.toISOString(),
        inventories: isRetail
          ? inventories
              .filter((item) => {
                const hasValidVariations = (item.variations || []).some(
                  (v) => v.price > 0 && v.quantity > 0,
                );
                return (
                  item.id &&
                  item.name &&
                  (hasValidVariations || (item.rate > 0 && item.quantity > 0))
                );
              })
              .map((i) => {
                if (isMobile) {
                  // Mobile logic
                  const amount = i.variations?.length
                    ? i.variations.reduce(
                        (sum, v) =>
                          sum +
                          (Number(v.price) || 0) * (Number(v.quantity) || 0),
                        0,
                      )
                    : (Number(i.rate) || 0) * (Number(i.quantity) || 0);

                  return {
                    id: i.id,
                    name: i.name,
                    amount,
                    quantity:
                      (i.variations || []).reduce(
                        (sum, v) => sum + (Number(v.quantity) || 0),
                        0,
                      ) || Number(i.quantity),
                    rate: Number(i.rate),
                    variations: (i.variations || [])
                      .filter((v) => v.price > 0 && v.quantity > 0)
                      .map((v) => ({
                        ...v,
                        price: Number(v.price),
                        quantity: Number(v.quantity),
                      })),
                  };
                } else {
                  // Desktop logic
                  const amount = i.variations?.length
                    ? i.variations.reduce(
                        (sum, v) =>
                          sum +
                          (Number(v.price) || 0) * (Number(v.quantity) || 0),
                        0,
                      )
                    : (Number(i.rate) || 0) * (Number(i.quantity) || 0);

                  return {
                    id: i.id,
                    name: i.name,
                    amount,
                    quantity: Number(i.quantity),
                    rate: Number(i.rate),
                    variations: (i.variations || []).map((v) => ({
                      ...v,
                      price: Number(v.price),
                      quantity: Number(v.quantity),
                    })),
                  };
                }
              })
          : [],
        services: !isRetail
          ? services
              .filter((service: { price: any[]; id: any; name: any }) => {
                const price = Array.isArray(service.price)
                  ? service.price.find((p) => p.currency === formData.currency)
                      ?.amount
                  : Number(service.price);
                return service.id && service.name && Number(price) > 0;
              })
              .map(
                (service: {
                  id: any;
                  name: any;
                  duration: any;
                  price: any[];
                }) => ({
                  id: service.id,
                  name: service.name,
                  duration: service.duration || "",
                  price: Array.isArray(service.price)
                    ? service.price.find(
                        (p) => p.currency === formData.currency,
                      )?.amount || 0
                    : Number(service.price),
                }),
              )
          : [],
        discount: Number(discount ? discount : 0),
        tax: Number(tax ? tax : 0),
        hasShipping: formData.hasShipping || !!formData.shippingCharge,
        shippingCharge: Number(formData.shippingCharge || 0),
        shippingDesc: formData.shippingDesc || "",
        status: formData.status || "pending",
        description: formData.description || "",
        additionalInformation: formData.additionalInformation || "",
        currency: formData.currency || "",
        exchangeRate: formData.exchangeRate || 1,
        paymentMethod: formData.paymentMethod || "",
        invoiceType: isRetail ? "inventory" : "service",
      };

      if (customer) {
        if (quoteNo) {
          const taxValue = formData.tax ? Number(formData.tax) / 100 : 0;
          const formattedTax = taxValue.toFixed(2);

          const updatedQuoteData = {
            expiryDate: validExpiry.toISOString(),
            issueDate: validIssueDate.toISOString(),
            discount: Number((formData.discount ?? 0) / 100),
            tax: Number(formattedTax),
            status: "Quote",
            hasShipping: formData.hasShipping || !!formData.shippingCharge,
            shippingCharge: Number(formData.shippingCharge || 0),
            shippingDesc: formData.shippingDesc || "",
            description: formData.description || "",
            currency: formData.currency || "NGN",
            exchangeRate:
              formData.exchangeRate && formData.exchangeRate >= 1
                ? formData.exchangeRate
                : 1,
            ...(isRetail && inventories.length
              ? {
                  inventories: inventories.map(
                    (i: {
                      id: string;
                      name: string;
                      rate: number;
                      quantity: number;
                      variations?: any;
                      amount?: number;
                    }) => ({
                      id: i.id,
                      name: i.name,
                      amount: i.amount || 0,
                      quantity: Number(i.quantity),
                      rate: Number(i.rate),
                      variations:
                        i.variations.length > 0 ? i.variations : undefined,
                    }),
                  ),
                }
              : {}),
            ...(!isRetail && services.length
              ? {
                  services: services
                    .filter((s: { price: any[]; id: any; name: any }) => {
                      const price = Array.isArray(s.price)
                        ? s.price.find((p) => p.currency === formData.currency)
                            ?.amount
                        : Number(s.price);
                      return s.id && s.name && Number(price) > 0;
                    })
                    .map((s: any) =>
                      normalizeServicePrice(s, formData.currency || ""),
                    ),
                }
              : []),
          };

          const createdUpdatedQuote = await updateQuote({
            id: quoteNo,
            data: updatedQuoteData,
          });
          const createdUpdatedQuoteId = createdUpdatedQuote?.id;
          const createdEmail = customer.email || "";
          setCreatedQuote(createdUpdatedQuoteId);
          setCreatedEmail(createdEmail);
          setBannerMessage("Quote Updated Successfully,");
          setBannerLinkText("View Quote");
          setIsBannerVisible(true);
        } else {
          const createdQuote = await createQuoteData(
            quoteData,
            customer,
            isRetail,
            quoteNo,
          );

          const createdQuoteId = createdQuote?.id;
          const createdEmail = customer.email || "";
          setCreatedQuote(createdQuoteId);
          setCreatedEmail(createdEmail);

          setBannerMessage("New Quote Created,");
          setBannerLinkText("View Quote");
          setIsBannerVisible(true);
        }
      }
    } catch (error: any) {
      handleErrors(error, setErrorMessage);
      showToast("An error occurred while processing the quote.", "error");
      if (error.response) {
        const errorMessage = error.response.data;
        console.error("API Error Details:", error.response?.data);
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateQuote = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    const basicInfoValid = validateQuoteBasicInfo(
      formData,
      setValidationResults,
    );

    if (!basicInfoValid) {
      setErrorMessage("Please fill out all required fields correctly.");
      showToast("Please fill out all required fields correctly.", "error");

      // Scroll to the first error
      const errorElements = [
        { id: "customer-info", hasError: !basicInfoValid },
        { id: "quote-date", hasError: !basicInfoValid },
      ];

      const firstErrorElement = errorElements.find((el) => el.hasError);
      if (firstErrorElement) {
        const element = document.getElementById(firstErrorElement.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      return;
    }

    if (
      (isRetail && formData.inventories.length === 0) ||
      (!isRetail && formData.services.length === 0)
    ) {
      setErrorMessage("At least one item is required");
      showToast("Please add at least one item", "error");
      return;
    }

    const validInventories = formData.inventories.filter((item) => {
      const hasValidVariations = item.variations?.some(
        (v) => v.price > 0 && v.quantity > 0,
      );
      return (
        item.id &&
        item.name &&
        (hasValidVariations || (item.rate > 0 && item.quantity > 0))
      );
    });

    const validServices = !isRetail
      ? formData.services.filter(
          (service: { price: any[]; id: any; name: any }) => {
            const price = Array.isArray(service.price)
              ? service.price.find((p) => p.currency === formData.currency)
                  ?.amount
              : Number(service.price);
            return service.id && service.name && Number(price) > 0;
          },
        )
      : [];

    const hasValidItems = isRetail
      ? validInventories.length > 0
      : validServices.length > 0;

    if (!hasValidItems) {
      setErrorMessage("At least one valid item is required");
      showToast("Please add at least one valid item", "error");
      return;
    }

    const updatedFormData = {
      ...formData,
      inventories: validInventories,
      services: validServices,
    };

    try {
      const basicInfoValid = validateQuoteBasicInfo(
        formData,
        setValidationResults,
      );
      const itemDetailsValid = validateQuoteItemDetails(
        updatedFormData,
        setValidationResults,
        userStore.isRetailBusiness(),
      );
      const summaryValid = validateQuoteSummary(formData, setValidationResults);

      if (basicInfoValid && itemDetailsValid && summaryValid) {
        setShowSidebar(true);
      } else {
        setErrorMessage("Please fill out fields correctly.");
        showToast("Please fill out fields correctly.", "error");
      }
    } catch (error) {
      console.error("Failed to create quote:", error);
      showToast("Failed to create quote. Please try again later.", "error");
    }
  };

  const onCreateQuote = async () => {
    await processCreateQuote(
      sendEmail,
      markAsPaid,
      shouldSendWhatsApp,
      shouldSendSMS,
      quoteNo,
    );

    resetForm();
    setIsBannerVisible(true);
    setShowSidebar(false);
  };

  function handleSendEmailChange(checked: boolean) {
    setSendEmail(checked);
  }
  function handleMarkAsPaidChange(event: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) {
    setMarkAsPaid(event.target.checked);
  }
  function handleshouldSendWhatsAppChange(checked: boolean) {
    setShouldSendWhatsApp(checked);
  }
  function handleshouldSendSMSChange(checked: boolean) {
    setShouldSendSMS(checked);
  }
  const handleIsReoccurringChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    setFormData((prevData) => ({
      ...prevData,
      isReoccurring: target.checked,
    }));
    setIsReoccurringChecked(target.checked);
  };

  const handlePaymentDropDownChange = (name: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const [selectedInventory, setSelectedInventory] = useState<IInventory | null>(
    null,
  );

  return (
    <>
      {isBannerVisible && (
        <SuccessBanner
          message={bannerMessage}
          linkText={bannerLinkText}
          onLinkClick={handleViewQuote}
          onClose={handleCloseBanner}
        />
      )}
      <div className="flex items-left gap-[8.5px] ml-[2em]">
        <button onClick={() => navigate(-1)}>
          <svg
            className="mt-[-2px] ml-[-2em] "
            width="10"
            height="16"
            viewBox="0 0 10 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8.5 1L1.5 8L8.5 15" stroke="#33363F" strokeWidth="2" />
          </svg>
        </button>
        <h1 className="lg:block md:block flex items-center xl:text-[20px] text-[16px] ml-[-1em] mt-[-0.4em] xl:mt-[-0.4em] font-bold">
          {isEditing ? " Edit Quote" : " Create Quote"}
        </h1>
      </div>
      <div
        className="mx-auto h-full bg-white lg:ml-0 border-2 border-gray-100 mt-3 "
        id="quote-parent"
      >
        {errorMessage && (
          <p className="text-[red] lg:pl-[3em] pl-3 md:pl-[3em] mt-3">
            {errorMessage}
          </p>
        )}

        <div className="w-full hidden md:flex items-end justify-end pt-[4em]">
          <h1 className="xl:text-[24px] biglg:text-[24px] text-[14px] font-semibold lg:px-[2em] pr-3 md:pr-[3em]">
            QUOTE
          </h1>
        </div>

        <div className="md:flex md:justify-between lg:pt-[4em] pt-[2em] ">
          <div className="lg:pl-[3em] px-3 md:pl-[3em]">
            <h1 className="font-semibold">Your Logo</h1>
            <LogoComponent
              logoUrl={formData.logoBase64 || ""}
              setLogoUrl={setLogoUrl}
              error={undefined}
              onUploadSuccess={handleEditProfileLogo}
            />
          </div>
        </div>

        <div className="hidden lg:block md:block">
          <div className="flex justify-between ">
            <div className="flex flex-col mt-[1em]">
              <div className="pl-3 md:pl-[3em] ">
                <CompanyUpdate
                  show={true}
                  toolTipText={t("businessNameTooltip")}
                  toolTipText2={t("businessAddressTooltip")}
                  toolTipText3={t("phoneNumberTooltip")}
                  toolTipText4={t("RCNumberTooltip")}
                />
              </div>
              <div
                id="customer-info"
                className="lg:pl-[3em] pl-3 md:pl-[3em] mt-[-1em]"
              >
                <CustomerCreateInfo
                  handleSetCustomer={handleSetCustomer}
                  formData={formData}
                  customer={customer}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  toolTipText={t("billTooltip")}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div id="quote-date" className="pr-[1em] md:pr-[4em] mt-[-10em]">
                <QuoteDate
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  toolTipText={t("quote.IssuedTooltip")}
                  toolTipText2={t("quote.dueTooltip")}
                  handleInputChange={handleInputChange}
                />
              </div>
              <div id="invoice-currency" className="lg:mt-[1em]">
                <InvoiceCurrency
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  showRateInput={showRateInput}
                  toolTipText={t("currencyTooltip")}
                  currencyList={currencyList}
                  localCurrency={userCurrencyCode.currency}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="lg:hidden md:hidden">
          <hr className="my-[1em]" />
          <div className="flex  flex-col-reverse justify-between ">
            <div className="flex flex-col ">
              <hr className="my-[1em]" />
              <div
                id="invoice-currency"
                className="lg:mt-[3em] mt-[-1em] px-[1em]"
              >
                <InvoiceCurrency
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  showRateInput={showRateInput}
                  currencyList={currencyList}
                  localCurrency={userCurrencyCode.currency}
                />
              </div>
            </div>
            <div className="">
              <div id="quote-date" className="px-[1em] md:pr-[3em]">
                <QuoteDate
                  handleDropDownChange={handleDropDownChange}
                  formData={formData}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  setValidationResults={setValidationResults}
                  validationResults={validationResults}
                  setFormData={setFormData}
                  toolTipText={t("quote.IssuedTooltip")}
                  toolTipText2={t("quote.dueTooltip")}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <hr className="my-[1em]" />

          <div className="px-[1em] mt-[2em]">
            <CompanyUpdate show={true} />
          </div>

          <hr className="my-[1em]" />
          <div
            id="customer-info"
            className="lg:pl-[3em] px-[1em] md:pl-[3em] mt-[-1em]"
          >
            <CustomerCreateInfo
              handleSetCustomer={handleSetCustomer}
              formData={formData}
              customer={customer}
              setValidationResults={setValidationResults}
              validationResults={validationResults}
            />
          </div>
        </div>
        {/* <hr className="lg:hidden md:hidden mt-[2em]" /> */}
        {/* <p className="lg:hidden md:hidden text-[9px] font-semibold flex justify-center text-center mt-3">
          Item Details
        </p> */}
        <div
          id="line-table"
          className="lg:mt-[4em] mt-[1em] md:px-[3em] hidden md:block"
        >
          <LineTable
            items={items}
            addItem={addItem}
            setItems={setItems}
            removeItem={removeItem}
            updateCell={updateCell}
            updateRow={updateRow}
            formData={formData}
            setFormData={setFormData}
            quoteNo={quoteNo}
            validationResults={validationResults}
            setValidationResults={setValidationResults}
            selectedCurrencyLabel={selectedCurrencyLabel}
            serviceType={serviceType as "time-based" | "session-based"}
          />
        </div>

        <div className="md:hidden px-[1em]">
          {isRetail ? (
            <LineItemsSection
              formData={formData}
              setFormData={setFormData}
              variations={[]}
              total={selectedInventory?.total || 0}
            />
          ) : (
            <div>
              <hr className="my-4" />
              <QuoteServiceLine formData={formData} setFormData={setFormData} />
              <hr className="my-4" />
            </div>
          )}
        </div>

        <div className="lg:flex lg:justify-end ">
          <div className="lg:pr-[3em] px-[1em]">
            <InvoiceCalculation
              subTotal={subTotal}
              formData={formData}
              calcDiscount={calcDiscount}
              calcTax={calcTax}
              calcShipping={calcShipping}
              total={total}
              handleInputChange={handleInputChange}
              isRetail={isRetail}
            />
          </div>
        </div>
        <hr className="border mt-[3em]" />
        <div className="hidden lg:block md:block">
          <div className=" flex flex-col justify-between md:gap-[2em]">
            <div className="pl-[3em]">
              <BankDetails />
            </div>
            <hr className="" />
            <div className="pl-[3em]">
              <Notes
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>
          </div>
        </div>
        <div className="lg:hidden md:hidden ] flex flex-col">
          <div className="px-[1em]">
            <Notes formData={formData} handleInputChange={handleInputChange} />
          </div>
          <hr className="my-[1.3em]" />
          <div className="px-[1em]">
            <BankDetails />
          </div>
        </div>
        <hr className="border mt-[3em]" />
        <div className="flex justify-end  pb-6 pr-[3em]">
          <div className="hidden md:block mt-5 ">
            <Button
              text={
                isLoading ? "Saving..." : isEditing ? "Save" : "Create Quote"
              }
              size="small"
              type="submit"
              onClick={isEditing ? onCreateQuote : handleCreateQuote}
              variant="secondary"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className=" flex justify-end  pb-6 pr-[1em] md:hidden lg:hidden">
          <div className="md:hidden mt-5 mb-4">
            <Button
              text={
                isLoading ? "Saving..." : isEditing ? "Save" : "Create Quote"
              }
              size="small"
              type="submit"
              onClick={isEditing ? onCreateQuote : handleCreateQuote}
              variant="secondary"
              disabled={isLoading}
            />
          </div>
        </div>
        {showSidebar && (
          <CreateModal>
            <CreateQuote
              formData={formData}
              handleInputChange={handleInputChange}
              handlePaymentDropDownChange={handlePaymentDropDownChange}
              handleIsReoccurringChange={handleIsReoccurringChange}
              validationResults={validationResults}
              invoiceNo={invoiceNo}
              sendEmail={sendEmail}
              markAsPaid={markAsPaid}
              shouldSendWhatsApp={shouldSendWhatsApp}
              shouldSendSMS={shouldSendSMS}
              handleMarkAsPaidChange={handleMarkAsPaidChange}
              handleSendEmailChange={handleSendEmailChange}
              handleshouldSendWhatsAppChange={handleshouldSendWhatsAppChange}
              handleshouldSendSMSChange={handleshouldSendSMSChange}
              errorMessage={errorMessage}
              error={error}
              customer={customer}
              showMarkAsPaid={true}
              onCreateQuote={onCreateQuote}
              isLoading={isLoading}
              onClose={() => setShowSidebar(false)}
              setValidationResults={setValidationResults}
            />
          </CreateModal>
        )}
      </div>
    </>
  );
};

export default QuoteParent;
